import { App } from 'antd';
import type { ArgsProps } from 'antd/es/notification/interface';

/**
 * Default notification options for the application
 * These are merged with user-provided options
 */
const DEFAULT_NOTIFICATION_OPTIONS: Partial<ArgsProps> = {
  placement: 'topRight',
  // Add other defaults here as needed:
  // duration: 4.5,
  // closable: true,
};

/**
 * Notification configuration options
 */
export interface NotificationOptions extends Partial<ArgsProps> {}

/**
 * Merge user options with defaults
 */
const mergeOptions = (options: NotificationOptions): ArgsProps => {
  return {
    ...DEFAULT_NOTIFICATION_OPTIONS,
    ...options,
  } as ArgsProps;
};

/**
 * Hook-based notification utilities
 * Use this in React components for context-aware notifications
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { notifySuccess, notifyError } = useNotifications();
 *
 *   const handleClick = () => {
 *     notifySuccess({ title: 'Success!', description: 'Operation completed' });
 *   };
 *
 *   return <Button onClick={handleClick}>Click me</Button>;
 * };
 * ```
 *
 * For custom notification needs, use App.useApp() directly:
 * ```tsx
 * const { notification } = App.useApp();
 * notification.open({ ...customOptions });
 * ```
 */
export const useNotifications = () => {
  const { notification: notificationApi } = App.useApp();

  const destroyNotification = (key: string) => {
    notificationApi.destroy(key);
  };

  const open = (options: NotificationOptions) => {
    notificationApi.open(mergeOptions(options));
  };

  const notifySuccess = (options: NotificationOptions) => {
    notificationApi.success(mergeOptions(options));
  };

  const notifyError = (options: NotificationOptions) => {
    notificationApi.error(mergeOptions(options));
  };

  const notifyInfo = (options: NotificationOptions) => {
    notificationApi.info(mergeOptions(options));
  };

  const notifyWarning = (options: NotificationOptions) => {
    notificationApi.warning(mergeOptions(options));
  };

  return {
    notify: open,
    notifySuccess,
    notifyError,
    notifyInfo,
    notifyWarning,
    destroyNotification,
  };
};
